import React, { useState, useEffect } from 'react';
import { useAuth, useOrganization } from '@clerk/clerk-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Header, Segment, Button, Icon, Message, Loader, Form, Input, Dropdown, List, Modal, TextArea, Grid, Divider, Label } from 'semantic-ui-react';
import { TestUiClient } from './testUi.client';
import { TestRunner } from './TestRunner';
import { DeleteButton } from '../components/DeleteButton';
const methodOptions = [
    { key: 'GET', text: 'GET', value: 'GET' },
    { key: 'POST', text: 'POST', value: 'POST' },
    { key: 'PUT', text: 'PUT', value: 'PUT' },
    { key: 'DELETE', text: 'DELETE', value: 'DELETE' },
    { key: 'PATCH', text: 'PATCH', value: 'PATCH' },
];
// File Upload Component
const FileUploader = ({ file, onFileChange, client }) => {
    const [uploading, setUploading] = useState(false);
    const fileInputRef = React.useRef(null);
    const handleFileSelect = async (event) => {
        const selectedFile = event.target.files?.[0];
        if (!selectedFile)
            return;
        try {
            setUploading(true);
            const uploadedFile = await client.uploadFile(selectedFile);
            onFileChange(uploadedFile);
        }
        catch (error) {
            console.error('File upload failed:', error);
            alert('File upload failed. Please try again.');
        }
        finally {
            setUploading(false);
            // Reset input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };
    const handleRemoveFile = () => {
        onFileChange(null);
    };
    return (React.createElement("div", null,
        React.createElement("input", { ref: fileInputRef, type: "file", onChange: handleFileSelect, style: { display: 'none' }, accept: "*/*" }),
        file ? (React.createElement(Segment, null,
            React.createElement("div", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
                React.createElement("div", null,
                    React.createElement(Label, { color: "blue" },
                        React.createElement(Icon, { name: "file" }),
                        file.fileName),
                    React.createElement("div", { style: { fontSize: '0.8em', color: '#666', marginTop: '0.25rem' } },
                        file.contentType,
                        " \u2022 ",
                        Math.round(file.fileSize / 1024),
                        " KB")),
                React.createElement(DeleteButton, { onConfirm: handleRemoveFile, buttonText: "Remove", title: "Remove File", message: "Are you sure you want to remove this file?" })))) : (React.createElement(Button, { onClick: () => fileInputRef.current?.click(), loading: uploading, disabled: uploading, style: { width: '100%' } },
            React.createElement(Icon, { name: "upload" }),
            uploading ? 'Uploading...' : 'Upload File'))));
};
export const TestDetailPage = () => {
    const { testId } = useParams();
    const { getToken } = useAuth();
    const { organization } = useOrganization();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [test, setTest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [testResults, setTestResults] = useState(null);
    // Form fields
    const [testName, setTestName] = useState('');
    const [path, setPath] = useState('');
    const [method, setMethod] = useState('GET');
    const [headers, setHeaders] = useState('{}');
    const [testCases, setTestCases] = useState([]);
    // Test case modal
    const [testCaseModalOpen, setTestCaseModalOpen] = useState(false);
    const [editingTestCase, setEditingTestCase] = useState(null);
    const [editingIndex, setEditingIndex] = useState(null);
    // Test case form
    const [caseName, setCaseName] = useState('');
    const [caseFile, setCaseFile] = useState(null);
    const [caseBody, setCaseBody] = useState('{}');
    const [caseExpected, setCaseExpected] = useState('{}');
    useEffect(() => {
        const initializeClient = async () => {
            if (!testId) {
                setError('Test ID is required');
                setLoading(false);
                return;
            }
            try {
                const testUiClient = new TestUiClient();
                setClient(testUiClient);
                const testData = await testUiClient.getTest(testId);
                setTest(testData);
                // Initialize form fields
                setTestName(testData.testName);
                setPath(testData.path);
                setMethod(testData.method);
                setHeaders(JSON.stringify(testData.headers || {}, null, 2));
                setTestCases(testData.testCases || []);
                // Load saved test results if they exist
                try {
                    const savedResults = await testUiClient.getTestResults(testId);
                    if (savedResults) {
                        setTestResults(savedResults);
                    }
                }
                catch (err) {
                    // Ignore errors loading test results - they may not exist yet
                    console.warn('No saved test results found:', err);
                }
            }
            catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load test');
            }
            finally {
                setLoading(false);
            }
        };
        initializeClient();
    }, [testId, getToken, organization?.id]);
    const handleSaveTest = async () => {
        if (!client || !test)
            return;
        try {
            setSaving(true);
            let parsedHeaders = {};
            try {
                parsedHeaders = JSON.parse(headers);
            }
            catch {
                setError('Invalid JSON in headers field');
                return;
            }
            await client.updateTest(test.testId, {
                testName,
                path,
                method,
                headers: parsedHeaders,
                testCases
            });
            // Refresh test data
            const updatedTest = await client.getTest(test.testId);
            setTest(updatedTest);
            setError(null);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save test');
        }
        finally {
            setSaving(false);
        }
    };
    const handleOpenTestCaseModal = (testCase, index) => {
        if (testCase && typeof index === 'number') {
            setEditingTestCase(testCase);
            setEditingIndex(index);
            setCaseName(testCase.name || '');
            setCaseFile(testCase.request.file || null);
            setCaseBody(JSON.stringify(testCase.request.body || {}, null, 2));
            setCaseExpected(JSON.stringify(testCase.expected, null, 2));
        }
        else {
            setEditingTestCase(null);
            setEditingIndex(null);
            setCaseName('');
            setCaseFile(null);
            setCaseBody('{}');
            setCaseExpected('{}');
        }
        setTestCaseModalOpen(true);
    };
    const handleCreatePresetTestCase = (presetType) => {
        setEditingTestCase(null);
        setEditingIndex(null);
        switch (presetType) {
            case 'process-text':
                setCaseName('Process Text Test');
                setCaseFile(null);
                setCaseBody(JSON.stringify({
                    text: "John Doe, age 35, lives at 123 Main Street, Anytown, NY 12345. Email: john.doe@email.com, Phone: (555) 123-4567",
                    formKey: "sample-form"
                }, null, 2));
                setCaseExpected(JSON.stringify({
                    name: "John Doe",
                    age: 35,
                    address: "123 Main Street, Anytown, NY 12345",
                    email: "john.doe@email.com",
                    phone: "(555) 123-4567"
                }, null, 2));
                if (path !== '/api/fluid-form/process-text') {
                    setPath('/api/fluid-form/process-text');
                    setMethod('POST');
                }
                break;
            case 'process-pdf':
                setCaseName('Process PDF Test');
                setCaseFile(null);
                setCaseBody(JSON.stringify({
                    formKey: "sample-form"
                }, null, 2));
                setCaseExpected(JSON.stringify({
                    name: "John Doe",
                    email: "john.doe@email.com",
                    phone: "(555) 123-4567",
                    address: "123 Main Street, Anytown, NY 12345"
                }, null, 2));
                if (path !== '/api/fluid-form/process-pdf') {
                    setPath('/api/fluid-form/process-pdf');
                    setMethod('POST');
                }
                break;
            default:
                setCaseName('');
                setCaseFile(null);
                setCaseBody('{}');
                setCaseExpected('{}');
        }
        setTestCaseModalOpen(true);
    };
    const handleSaveTestCase = async () => {
        try {
            const body = JSON.parse(caseBody);
            const expected = JSON.parse(caseExpected);
            console.log('Saving test case with file:', caseFile);
            const newTestCase = {
                name: caseName,
                request: {
                    file: caseFile || undefined, // Use undefined instead of null to avoid empty objects
                    body
                },
                expected
            };
            console.log('New test case:', newTestCase);
            let updatedTestCases;
            if (editingIndex !== null) {
                // Update existing test case
                updatedTestCases = [...testCases];
                updatedTestCases[editingIndex] = newTestCase;
            }
            else {
                // Add new test case
                updatedTestCases = [...testCases, newTestCase];
            }
            setTestCases(updatedTestCases);
            setTestCaseModalOpen(false);
            // Auto-save the test after adding/editing test case
            if (client && test) {
                try {
                    let parsedHeaders = {};
                    try {
                        parsedHeaders = JSON.parse(headers);
                    }
                    catch {
                        // Use existing headers if parsing fails
                        parsedHeaders = test.headers || {};
                    }
                    await client.updateTest(test.testId, {
                        testName,
                        path,
                        method,
                        headers: parsedHeaders,
                        testCases: updatedTestCases
                    });
                    // Refresh test data
                    const updatedTest = await client.getTest(test.testId);
                    setTest(updatedTest);
                }
                catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to auto-save test');
                }
            }
        }
        catch (err) {
            setError('Invalid JSON in test case fields');
        }
    };
    const handleDeleteTestCase = async (index) => {
        if (confirm('Are you sure you want to delete this test case?')) {
            const updatedTestCases = testCases.filter((_, i) => i !== index);
            setTestCases(updatedTestCases);
            // Auto-save the test after deleting test case
            if (client && test) {
                try {
                    let parsedHeaders = {};
                    try {
                        parsedHeaders = JSON.parse(headers);
                    }
                    catch {
                        parsedHeaders = test.headers || {};
                    }
                    await client.updateTest(test.testId, {
                        testName,
                        path,
                        method,
                        headers: parsedHeaders,
                        testCases: updatedTestCases
                    });
                    // Refresh test data
                    const updatedTest = await client.getTest(test.testId);
                    setTest(updatedTest);
                }
                catch (err) {
                    setError(err instanceof Error ? err.message : 'Failed to auto-save test');
                }
            }
        }
    };
    const handleRunTest = async () => {
        if (!client || !test)
            return;
        // Auto-save before running test
        try {
            let parsedHeaders = {};
            try {
                parsedHeaders = JSON.parse(headers);
            }
            catch {
                parsedHeaders = test.headers || {};
            }
            await client.updateTest(test.testId, {
                testName,
                path,
                method,
                headers: parsedHeaders,
                testCases
            });
            // Refresh test data
            const updatedTest = await client.getTest(test.testId);
            setTest(updatedTest);
            // Now run the test
            setTestResults(null);
            const result = await client.runTest(updatedTest);
            setTestResults(result);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to run test');
        }
    };
    if (loading) {
        return (React.createElement(Container, { fluid: true, style: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(Segment, { basic: true, textAlign: "center" },
                React.createElement(Loader, { active: true, size: "large" }, "Loading test..."))));
    }
    if (!test) {
        return (React.createElement(Container, { fluid: true, style: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' } },
            React.createElement(Message, { negative: true },
                React.createElement(Message.Header, null, "Test not found"),
                React.createElement("p", null, "The requested test could not be found."),
                React.createElement(Button, { onClick: () => navigate('/test-ui') }, "Back to Tests"))));
    }
    return (React.createElement(Container, { fluid: true, style: { minHeight: '100vh', backgroundColor: '#f8f9fa', padding: '2rem' } },
        React.createElement(Header, { as: "h1", icon: true },
            React.createElement(Icon, { name: "edit" }),
            React.createElement(Header.Content, null,
                test.testName,
                React.createElement(Header.Subheader, null, "Configure test settings and test cases"))),
        React.createElement(Button, { basic: true, onClick: () => navigate('/test-ui'), style: { marginBottom: '1rem' } },
            React.createElement(Icon, { name: "arrow left" }),
            "Back to Tests"),
        error && (React.createElement(Message, { negative: true, onDismiss: () => setError(null), style: { marginBottom: '2rem' } },
            React.createElement(Message.Header, null, "Error"),
            React.createElement("p", null, error))),
        React.createElement(Grid, null,
            React.createElement(Grid.Column, { width: 6 },
                React.createElement(Segment, null,
                    React.createElement(Header, { as: "h3", dividing: true },
                        React.createElement(Icon, { name: "settings" }),
                        "Test Configuration"),
                    React.createElement(Form, null,
                        React.createElement(Form.Field, null,
                            React.createElement("label", null, "Test Name"),
                            React.createElement(Input, { value: testName, onChange: (e) => setTestName(e.target.value), placeholder: "Enter test name" })),
                        React.createElement(Form.Group, { widths: "equal" },
                            React.createElement(Form.Field, null,
                                React.createElement("label", null, "HTTP Method"),
                                React.createElement(Dropdown, { selection: true, options: methodOptions, value: method, onChange: (e, { value }) => setMethod(value) })),
                            React.createElement(Form.Field, null,
                                React.createElement("label", null, "API Path"),
                                React.createElement(Input, { value: path, onChange: (e) => setPath(e.target.value), placeholder: "/api/endpoint" }))),
                        React.createElement(Form.Field, null,
                            React.createElement("label", null, "Headers (JSON)"),
                            React.createElement(TextArea, { value: headers, onChange: (e) => setHeaders(e.target.value), placeholder: '{"Content-Type": "application/json"}', rows: 4, style: { fontFamily: 'monospace' } })),
                        React.createElement(Button, { primary: true, loading: saving, onClick: handleSaveTest, disabled: !testName.trim() || !path.trim() },
                            React.createElement(Icon, { name: "save" }),
                            "Save Test"))),
                React.createElement(Segment, null,
                    React.createElement(Header, { as: "h3", dividing: true },
                        React.createElement(Icon, { name: "clipboard list" }),
                        "Test Cases",
                        React.createElement(Button, { primary: true, floated: "right", size: "small", onClick: () => handleOpenTestCaseModal() },
                            React.createElement(Icon, { name: "plus" }),
                            "Add Test Case")),
                    testCases.length === 0 ? (React.createElement(Message, { info: true },
                        React.createElement(Message.Header, null, "No test cases yet"),
                        React.createElement("p", null, "Add test cases to define the expected behavior of your API endpoint."))) : (React.createElement(List, { divided: true, relaxed: true }, testCases.map((testCase, index) => (React.createElement(List.Item, { key: index },
                        React.createElement(List.Content, null,
                            React.createElement(List.Header, null, testCase.name || `Test Case ${index + 1}`),
                            React.createElement(List.Description, { style: { marginTop: '0.5rem' } },
                                React.createElement("div", { style: { fontFamily: 'monospace', fontSize: '0.9em', color: '#666' } },
                                    "Request: ",
                                    JSON.stringify(testCase.request).substring(0, 100),
                                    "...")),
                            React.createElement("div", { style: { marginTop: '0.5rem' } },
                                React.createElement(Button, { size: "mini", onClick: () => handleOpenTestCaseModal(testCase, index) },
                                    React.createElement(Icon, { name: "edit" }),
                                    "Edit"),
                                React.createElement(DeleteButton, { onConfirm: () => handleDeleteTestCase(index), style: { marginLeft: '0.5rem' }, title: "Delete Test Case", message: "Are you sure you want to delete this test case? This action cannot be undone." })))))))))),
            React.createElement(Grid.Column, { width: 10 },
                React.createElement(TestRunner, { test: test, onRun: handleRunTest, results: testResults }))),
        React.createElement(Modal, { size: "large", open: testCaseModalOpen, onClose: () => setTestCaseModalOpen(false) },
            React.createElement(Modal.Header, null,
                React.createElement(Icon, { name: editingTestCase ? "edit" : "plus" }),
                editingTestCase ? 'Edit Test Case' : 'Add Test Case'),
            React.createElement(Modal.Content, null,
                !editingTestCase && (React.createElement(Segment, { style: { marginBottom: '1rem' } },
                    React.createElement(Header, { as: "h4" },
                        React.createElement(Icon, { name: "magic" }),
                        "Quick Start Templates"),
                    React.createElement(Message, { info: true },
                        React.createElement(Message.Header, null, "Create test cases for common endpoints"),
                        React.createElement("p", null, "Click a button below to automatically populate the test case with a typical request structure.")),
                    React.createElement(Button.Group, null,
                        React.createElement(Button, { color: "blue", onClick: () => handleCreatePresetTestCase('process-text') },
                            React.createElement(Icon, { name: "file text" }),
                            "/process-text"),
                        React.createElement(Button, { color: "orange", onClick: () => handleCreatePresetTestCase('process-pdf') },
                            React.createElement(Icon, { name: "file pdf" }),
                            "/process-pdf")),
                    React.createElement(Divider, null))),
                React.createElement(Form, null,
                    React.createElement(Form.Field, null,
                        React.createElement("label", null, "Test Case Name"),
                        React.createElement(Input, { value: caseName, onChange: (e) => setCaseName(e.target.value), placeholder: "Enter test case name" })),
                    React.createElement(Form.Field, null,
                        React.createElement("label", null, "File Upload (Optional)"),
                        client && (React.createElement(FileUploader, { file: caseFile, onFileChange: setCaseFile, client: client }))),
                    React.createElement(Form.Field, null,
                        React.createElement("label", null, "Request Body (JSON)"),
                        React.createElement(TextArea, { value: caseBody, onChange: (e) => setCaseBody(e.target.value), rows: 6, style: { fontFamily: 'monospace' }, placeholder: '{"key": "value"}' })),
                    React.createElement(Form.Field, null,
                        React.createElement("label", null, "Expected Response (JSON)"),
                        React.createElement(TextArea, { value: caseExpected, onChange: (e) => setCaseExpected(e.target.value), rows: 6, style: { fontFamily: 'monospace' }, placeholder: '{"status": "success", "data": {...}}' })))),
            React.createElement(Modal.Actions, null,
                React.createElement(Button, { onClick: () => setTestCaseModalOpen(false) }, "Cancel"),
                React.createElement(Button, { primary: true, onClick: handleSaveTestCase, disabled: !caseName.trim() },
                    React.createElement(Icon, { name: "check" }),
                    "Save Test Case")))));
};
